import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import axios from '../lib/axios';

// Types for store data
interface Store {
  id: number;
  name: string;
  shop: string;
  domain?: string;
  user_role: 'owner' | 'admin' | 'member' | 'viewer';
  connected: boolean;
  created_at: string;
  permissions?: {
    sight_access?: boolean;
    billing_access?: boolean;
    settings_access?: boolean;
    integrations_access?: boolean;
    scaleboard_access?: boolean;
    support_access?: boolean;
    isight_access?: boolean;
    [key: string]: boolean | undefined;
  };
  // Pending invitation metadata
  _isPending?: boolean;
  _invitationToken?: string;
  _expiresAt?: string;
}

interface ActiveStoreState {
  activeStore: Store | null;
  userStores: Store[];
  isLoading: boolean;
  error: string | null;
}

interface ActiveStoreContextType extends ActiveStoreState {
  setActiveStore: (store: Store) => void;
  refreshStores: () => Promise<void>;
  switchToStore: (storeId: number) => Promise<void>;
}

// Action types
type StoreAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_STORES'; payload: Store[] }
  | { type: 'SET_ACTIVE_STORE'; payload: Store }
  | { type: 'SWITCH_STORE'; payload: number };

// Initial state
const initialState: ActiveStoreState = {
  activeStore: null,
  userStores: [],
  isLoading: false,
  error: null,
};

// Synchronously hydrate activeStore from localStorage so permissions are
// available immediately on any entry (SPA navigation or full reload), not only
// after the async refreshStores() completes. refreshStores() will reconcile it
// shortly after with the fresh /api/stores payload.
//
// When a token exists, mark the initial load as in-flight (isLoading: true) so
// consumers (e.g. useEnhancedPermissions) know the store list is still being
// resolved and must NOT treat a null activeStore as a definitive "no access"
// answer on the very first render.
function initActiveStoreState(): ActiveStoreState {
  const saved = localStorage.getItem('activeStore');
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      if (parsed && typeof parsed.id === 'number' && typeof parsed.shop === 'string') {
        return {
          ...initialState,
          activeStore: parsed as Store,
          isLoading: !!localStorage.getItem('token'),
        };
      }
    } catch (_) {
      // Ignore corrupt data and fall back to null
    }
  }
  return {
    ...initialState,
    isLoading: !!localStorage.getItem('token'),
  };
}

// Reducer
function storeReducer(state: ActiveStoreState, action: StoreAction): ActiveStoreState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    
    case 'SET_ERROR':
      return { ...state, error: action.payload };
    
    case 'SET_STORES':
      return { ...state, userStores: action.payload, error: null };
    
    case 'SET_ACTIVE_STORE':
      return { ...state, activeStore: action.payload, error: null };
    
    case 'SWITCH_STORE':
      const store = state.userStores.find(s => s.id === action.payload);
      return { 
        ...state, 
        activeStore: store || null, 
        error: store ? null : 'Store not found' 
      };
    
    default:
      return state;
  }
}

// Context
const ActiveStoreContext = createContext<ActiveStoreContextType | undefined>(undefined);

// Provider component
export function ActiveStoreProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(storeReducer, initialState, initActiveStoreState);

  // Actions
  const setActiveStore = (store: Store) => {
    dispatch({ type: 'SET_ACTIVE_STORE', payload: store });
    // console.log('Saving active store to localStorage:', { id: store.id, shop: store.shop, name: store.name });
    localStorage.setItem('activeStoreId', store.id.toString());
    localStorage.setItem('activeStoreShop', store.shop);
    // Save complete store object for other contexts
    localStorage.setItem('activeStore', JSON.stringify(store));
    // IMPORTANT: Also update AuthContext's shop key for compatibility
    localStorage.setItem('shop', store.shop);
    // console.log('Also synced to AuthContext shop key:', store.shop);
  };

  const refreshStores = async (): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const token = localStorage.getItem('token');
      if (!token) throw new Error('No authentication token found');

      // console.log('ActiveStoreContext: Fetching stores from /api/stores and pending invitations');
      
      // Fetch both user stores and pending invitations
      const [storesResponse, invitationsResponse] = await Promise.all([
        axios.get('/api/stores'),
        axios.get('/invitations/user/pending')
      ]);

      const userStores: Store[] = storesResponse.data;
      const pendingInvitations = invitationsResponse.data.invitations || [];
      
      // Removed excessive debug logging to prevent system hanging
      
      // Removed excessive debug logging to prevent system hanging
      
      if (!Array.isArray(userStores)) {
        console.error('Invalid stores format:', userStores);
        throw new Error('Invalid stores data received from API');
      }
      
      // Convert pending invitations to store format for display in switcher
      const pendingStores: Store[] = pendingInvitations.map((invitation: any) => ({
        id: invitation.merchant_id,
        name: invitation.merchant_name || invitation.merchant_shop?.replace('.myshopify.com', '') || 'Unknown Store',
        shop: invitation.merchant_shop,
        domain: null,
        user_role: invitation.role || 'member',
        connected: false, // Pending invitations are not connected
        created_at: invitation.created_at,
        permissions: {},
        // Add invitation metadata for handling
        _isPending: true,
        _invitationToken: invitation.token,
        _expiresAt: invitation.expires_at
      }));
      
      // Combine user stores and pending stores
      const allStores = [...userStores, ...pendingStores];
      
      // Removed excessive debug logging to prevent system hanging
      dispatch({ type: 'SET_STORES', payload: allStores });

      // Set active store from localStorage or first available
      const savedStoreId = localStorage.getItem('activeStoreId');
      const savedStoreShop = localStorage.getItem('activeStoreShop');
      // Removed excessive debug logging to prevent system hanging
      
      let activeStore = null;
      
      // Try to find by ID first
      if (savedStoreId) {
        activeStore = allStores.find((s: Store) => s.id === parseInt(savedStoreId));
        if (activeStore) {
          // Removed excessive debug logging to prevent system hanging
        }
      }
      
      // Fallback to shop name if ID not found
      if (!activeStore && savedStoreShop) {
        activeStore = allStores.find((s: Store) => s.shop === savedStoreShop);
        if (activeStore) {
          // Removed excessive debug logging to prevent system hanging
        }
      }
      
      // Final fallback to first store
      const firstStore = allStores.length > 0 ? allStores[0] : null;
      if (!activeStore && firstStore) {
        activeStore = firstStore;
      }
      
      if (activeStore) {
        setActiveStore(activeStore);
      }
    } catch (error) {
      console.error('Error fetching stores:', error);
      const errorMessage = axios.isAxiosError(error) 
        ? error.response?.data?.message || error.message || 'Failed to fetch stores'
        : error instanceof Error 
        ? error.message 
        : 'Unknown error';
      dispatch({ type: 'SET_ERROR', payload: errorMessage });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const switchToStore = async (storeId: number): Promise<void> => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // console.log('🔄 switchToStore called with ID:', storeId);
      // console.log('📦 Available stores:', state.userStores.map(s => ({ id: s.id, shop: s.shop, name: s.name })));
      
      const store = state.userStores.find(s => s.id === storeId);
      
      if (!store) {
        const errorMsg = `Store not found with ID: ${storeId}`;
        // console.error('❌ ' + errorMsg);
        throw new Error(errorMsg);
      }

      // console.log('✅ Found store, setting as active:', { id: store.id, shop: store.shop, name: store.name });
      setActiveStore(store);
      
      // Trigger store-specific data refresh
      window.dispatchEvent(new CustomEvent('storeChanged', { detail: store }));
      
    } catch (error) {
      const errorMsg = error instanceof Error ? error.message : 'Failed to switch store';
      // console.error('❌ Error in switchToStore:', errorMsg);
      dispatch({ type: 'SET_ERROR', payload: errorMsg });
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  // Initialize on mount and after login
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      refreshStores();
    }

    // Re-fetch stores whenever a login event fires (token wasn't present on mount)
    const handleLogin = () => refreshStores();
    window.addEventListener('auth_login', handleLogin);
    return () => window.removeEventListener('auth_login', handleLogin);
  }, []);

  const value: ActiveStoreContextType = {
    ...state,
    setActiveStore,
    refreshStores,
    switchToStore,
  };

  return (
    <ActiveStoreContext.Provider value={value}>
      {children}
    </ActiveStoreContext.Provider>
  );
}

// Hook
export function useActiveStore() {
  const context = useContext(ActiveStoreContext);
  if (context === undefined) {
    throw new Error('useActiveStore must be used within an ActiveStoreProvider');
  }
  return context;
}
